import React, { useEffect, useMemo, useRef, useState } from 'react'
import { styled } from '@mui/material/styles'
import { VideoTrack } from '@livekit/components-react'
import { Track } from 'livekit-client'
import { useVirtualRoomFilteredParticipants } from '../../hooks/useVirtualRoomFilteredParticipants'
import { Message } from '../../../../types/Messages'
import {
  VideoControlButton,
  MicOnIcon,
  MicOffIcon,
  CameraOnIcon,
  CameraOffIcon,
} from './VideoControlButton'
import { HandIcon, HandRaisedIndicator, handBadgeProps } from './HandRaisedIndicator'
import {
  CameraOffPicture,
  liveCameraTrack,
  remoteCameraOffPicture,
  useLocalCameraOffPicture,
} from '../LiveKitVideoDisplay/cameraOffPicture'
import { reconcileSidebarOrder, promoteSpeakersIntoViewport } from '../LiveKitVideoDisplay/utils/sidebarOrder'
import { selfViewVideoStyle } from '../../utils/selfViewMirror'

// NOTE: We accept the same track reference object returned by `useTracks`
export interface TrackReference {
  participant: any
  publication: any
  track: any
  trackSid: string
  source: Track.Source | undefined
}

type CameraViewMode = 'single' | 'conference'

// Panel tile layout constants (shared between styled components and promotion logic)
const SINGLE_TILE_HEIGHT = 150
const CONF_ROW_HEIGHT = 120
const PANEL_TILE_GAP = 8

interface ParticipantsPanelProps {
  /** Camera tracks ordered so local first, then recent speakers, etc. */
  sortedCameraTracks: TrackReference[]
  /** Same tracks but grouped into rows of two (only used for conference view) */
  groupedCameraTracks: TrackReference[][]
  /** All in-proximity participants (including those with camera off) */
  allParticipants?: any[]
  /** Map of participantId → camera track (for fast lookup) */
  cameraTracksByParticipantId?: Map<string, TrackReference>
  speakingIds: Set<string>
  handRaisedSessionIds?: Set<string>
  /** Identity of the participant currently sharing their screen */
  screenShareParticipantId?: string
  cameraViewMode: CameraViewMode
  audioEnabled: boolean
  videoEnabled: boolean
  toggleAudio: () => void
  toggleVideo: () => void
  /** The Colyseus room (hand flags live in its `players` schema), not the LiveKit room. */
  room?: any
  ownerSessionId?: string
  /**
   * Lets the local user lower other people's hands when the view knows they are
   * presenting but the schema cannot say so (whiteboard presenters have no
   * `screenShareSessionId`; the whiteboard view passes "opened it with R"). The
   * server decides separately, in `handRaisePolicy`: connected to a board in the
   * requester's virtual room or a public one, and the target in the same room.
   */
  canLowerHands?: boolean
  isLoading?: boolean
}

const ParticipantsPanel: React.FC<ParticipantsPanelProps> = ({
  sortedCameraTracks,
  groupedCameraTracks,
  allParticipants,
  cameraTracksByParticipantId,
  speakingIds,
  handRaisedSessionIds,
  screenShareParticipantId,
  cameraViewMode,
  audioEnabled,
  videoEnabled,
  toggleAudio,
  toggleVideo,
  room,
  ownerSessionId,
  canLowerHands = false,
  isLoading,
}) => {
  const { participants: hookParticipants, isParticipantInSameRoom } = useVirtualRoomFilteredParticipants()
  const localPicture = useLocalCameraOffPicture()
  const gridRef = useRef<HTMLDivElement>(null)
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)
  const tracksLengthRef = useRef<number>(0)

  // Use provided participants (preferred) or fall back to hook
  const resolvedParticipants = allParticipants ?? hookParticipants

  // Build a stable base order: local → screen sharer → hand-raised → alphabetical.
  // Speaking-based reordering is handled by the panelOrder promotion logic below.
  const sortedParticipants = useMemo(() => {
    return [...resolvedParticipants].sort((a, b) => {
      if (a.isLocal && !b.isLocal) return -1
      if (!a.isLocal && b.isLocal) return 1
      if (a.isLocal && b.isLocal) return 0

      const aSharingScreen = a.identity === screenShareParticipantId
      const bSharingScreen = b.identity === screenShareParticipantId
      if (aSharingScreen && !bSharingScreen) return -1
      if (!aSharingScreen && bSharingScreen) return 1

      const handRaisedA = room?.state?.players?.get(a.identity)?.handRaised ?? false
      const handRaisedB = room?.state?.players?.get(b.identity)?.handRaised ?? false

      if (handRaisedA && !handRaisedB) return -1
      if (!handRaisedA && handRaisedB) return 1

      // Unmuted participants above muted ones
      const aMuted = !a.isMicrophoneEnabled
      const bMuted = !b.isMicrophoneEnabled
      if (!aMuted && bMuted) return -1
      if (aMuted && !bMuted) return 1

      const nameA = a.name || a.identity || ''
      const nameB = b.name || b.identity || ''
      return nameA.localeCompare(nameB)
    })
  }, [resolvedParticipants, room, handRaisedSessionIds, screenShareParticipantId])

  // ── Stored panel order with off-screen speaker promotion ──

  const baseOrder = useMemo(
    () => sortedParticipants.map(p => p.identity),
    [sortedParticipants],
  )

  const [panelOrder, setPanelOrder] = useState<string[]>(baseOrder)

  useEffect(() => {
    setPanelOrder(prev => reconcileSidebarOrder(prev, baseOrder))
  }, [baseOrder])

  useEffect(() => {
    const grid = gridRef.current
    if (!grid) return

    const tileHeight = cameraViewMode === 'single'
      ? SINGLE_TILE_HEIGHT + PANEL_TILE_GAP
      : CONF_ROW_HEIGHT + PANEL_TILE_GAP
    const tilesPerRow = cameraViewMode === 'single' ? 1 : 2
    const visibleRows = Math.floor(grid.clientHeight / tileHeight)
    const viewportCapacity = visibleRows * tilesPerRow

    if (viewportCapacity <= 0) return

    const pinnedIds = new Set<string>()
    for (const p of resolvedParticipants) {
      if (p.isLocal) pinnedIds.add(p.identity)
    }
    if (screenShareParticipantId) pinnedIds.add(screenShareParticipantId)
    if (handRaisedSessionIds) {
      for (const id of handRaisedSessionIds) pinnedIds.add(id)
    }

    setPanelOrder(prev =>
      promoteSpeakersIntoViewport(prev, speakingIds, viewportCapacity, pinnedIds)
    )
  }, [speakingIds, cameraViewMode, resolvedParticipants, handRaisedSessionIds, screenShareParticipantId])

  // Build render list from stored order, ensuring local participant is always first
  const orderedParticipants = useMemo(() => {
    const byId = new Map(resolvedParticipants.map(p => [p.identity, p]))
    const ordered = panelOrder.map(id => byId.get(id)).filter(Boolean)
    const localIdx = ordered.findIndex(p => p!.isLocal)
    if (localIdx > 0) {
      const [local] = ordered.splice(localIdx, 1)
      ordered.unshift(local)
    }
    return ordered
  }, [panelOrder, resolvedParticipants])

  // Group ordered participants into rows of 2 for conference view
  const groupedParticipants = useMemo(() => {
    const rows: (typeof orderedParticipants)[] = []
    for (let i = 0; i < orderedParticipants.length; i += 2) {
      rows.push(orderedParticipants.slice(i, i + 2))
    }
    return rows
  }, [orderedParticipants])

  // Scroll indicator logic - separate effect for scroll/resize listeners (no dependencies)
  useEffect(() => {
    const checkScrollNeeded = () => {
      const el = gridRef.current
      if (!el) return
      const isScrollable = el.scrollHeight > el.clientHeight
      const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 5
      setShowScrollIndicator(isScrollable && !isAtBottom)
    }

    const el = gridRef.current
    if (!el) return

    // Defer initial check to avoid synchronous state update during render
    const timeoutId = setTimeout(() => {
      checkScrollNeeded()
    }, 0)

    el.addEventListener('scroll', checkScrollNeeded)
    const ro = new ResizeObserver(checkScrollNeeded)
    ro.observe(el)

    return () => {
      clearTimeout(timeoutId)
      el.removeEventListener('scroll', checkScrollNeeded)
      ro.disconnect()
    }
  }, []) // Empty deps - only set up listeners once

  // Separate effect to check scroll when content changes
  useEffect(() => {
    const currentLength = panelOrder.length

    // Only check if content length actually changed
    if (currentLength !== tracksLengthRef.current) {
      tracksLengthRef.current = currentLength

      // Defer check to after DOM update
      const timeoutId = setTimeout(() => {
        const el = gridRef.current
        if (!el) return
        const isScrollable = el.scrollHeight > el.clientHeight
        const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 5
        setShowScrollIndicator(isScrollable && !isAtBottom)
      }, 0)

      return () => clearTimeout(timeoutId)
    }
  }, [panelOrder.length, cameraViewMode])

  // Helpers
  const findName = (identity: string, isLocal: boolean): string => {
    // Safety check: ensure participant is in same room
    if (!isLocal && !isParticipantInSameRoom(identity)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[ParticipantsPanel] Cross-room participant detected in findName:', identity);
      }
      return 'Unknown'; // Should never happen, but safety check
    }
    const p = resolvedParticipants.find((pp) => pp.identity === identity)
    if (p?.name) return p.name
    return isLocal ? 'You' : identity.substring(0, 8)
  }

  /** Render a single participant tile – with live video if available, placeholder otherwise */
  const renderParticipantTile = (
    participant: any,
    containerEl: React.ElementType,
    containerKey: string
  ) => {
    const isLocal = participant.isLocal
    const identity = participant.identity
    const isSpeaking = speakingIds.has(identity)
    const speakingClass = isSpeaking ? (isLocal ? 'local-speaking' : 'speaking') : ''

    // Look up camera track from map (preferred) or fallback scan of sortedCameraTracks
    const cameraTrack = cameraTracksByParticipantId
      ? cameraTracksByParticipantId.get(identity)
      : sortedCameraTracks.find((t) => t.participant.identity === identity)
    const visibleCameraTrack = liveCameraTrack(cameraTrack, isLocal, videoEnabled, participant)

    const displayName = findName(identity, isLocal)
    const picture = isLocal
      ? localPicture
      : remoteCameraOffPicture({ identity, name: displayName, metadata: participant.metadata })

    const Container = containerEl as any
    const isMuted = !participant.isMicrophoneEnabled

    const playerSchema = room?.state?.players?.get(identity)
    const isHandRaised = playerSchema?.handRaised ?? false

    const localPlayer = room?.state?.players?.get(room?.sessionId)
    const isLocalOwner = ownerSessionId && room?.sessionId ? ownerSessionId === room.sessionId : false
    const isLocalPresenter = localPlayer?.screenShareSessionId && localPlayer?.screenShareSessionId !== ''
    const canLowerHand = isLocalOwner || isLocalPresenter || canLowerHands

    return (
      <Container key={containerKey} className={speakingClass}>
        {visibleCameraTrack ? (
          React.createElement(VideoTrack as any, {
            trackRef: visibleCameraTrack,
            style: selfViewVideoStyle(isLocal),
          })
        ) : (
          // Camera-off placeholder: dark background + profile photo, or initials without one
          <CameraOffPlaceholder data-testid={`camera-off-${identity}`}>
            <CameraOffPicture
              picture={picture}
              size="52px"
              style={isLocal ? { boxShadow: '0 0 0 2px var(--sevv-primary, #f97415)' } : undefined}
            />
            <CameraOffLabel>
              <CameraOffIcon />
            </CameraOffLabel>
          </CameraOffPlaceholder>
        )}
        {isMuted && (
          <MuteIndicator data-testid={`mute-indicator-${identity}`}>
            <MicOffIcon />
          </MuteIndicator>
        )}
        {isHandRaised && (
          <HandRaisedIndicator
            data-testid={`hand-raised-indicator-${identity}`}
            {...handBadgeProps(
              canLowerHand && room
                ? () => room.send(Message.RAISE_HAND, { raised: false, targetSessionId: identity })
                : undefined
            )}
          >
            <HandIcon />
          </HandRaisedIndicator>
        )}
        <ParticipantName data-testid={`participant-name-${identity}`}>
          {displayName} {isLocal && '(You)'}
          {isSpeaking && ' 🗣️'}
          {isHandRaised && ' ✋'}
        </ParticipantName>
      </Container>
    )
  }

  const SingleColumn = (
    <ParticipantsCameraGrid ref={gridRef}>
      {orderedParticipants.map((participant) =>
        renderParticipantTile(
          participant,
          CameraContainer,
          `camera-single-${participant.identity}`
        )
      )}
    </ParticipantsCameraGrid>
  )

  const Conference = (
    <ConferenceCameraGrid ref={gridRef}>
      {groupedParticipants.map((row, idx) => (
        <ConferenceCameraRow key={`row-${idx}`}>
          {row.map((participant) =>
            renderParticipantTile(
              participant,
              ConferenceCameraContainer,
              `camera-conf-${participant.identity}`
            )
          )}
        </ConferenceCameraRow>
      ))}
    </ConferenceCameraGrid>
  )

  if (isLoading) {
    return (
      <Wrapper>
        <LoadingWrapper>
          <Spinner />
          <LoadingText>Syncing participants...</LoadingText>
        </LoadingWrapper>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      {cameraViewMode === 'single' ? SingleColumn : Conference}
      <ScrollIndicator show={showScrollIndicator} />
    </Wrapper>
  )
}

export default ParticipantsPanel

/* Styled Components (lifted from original modal) */
const Wrapper = styled('div')({ position: 'relative' })

const LoadingWrapper = styled('div')({
  width: '300px',
  height: '100%',
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  color: 'rgba(255, 255, 255, 0.7)',
  padding: '24px',
})

const Spinner = styled('div')({
  width: '28px',
  height: '28px',
  border: '3px solid rgba(255, 255, 255, 0.1)',
  borderTop: '3px solid var(--sevv-primary, #f97415)',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
})

const LoadingText = styled('div')({
  fontSize: '13px',
  fontWeight: '500',
  color: 'rgba(255, 255, 255, 0.6)',
  textAlign: 'center',
})

const ParticipantsCameraGrid = styled('div')({
  width: '300px',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: `${PANEL_TILE_GAP}px`,
  padding: `${PANEL_TILE_GAP}px`,
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
    width: '0',
    height: '0',
  },
})

const CameraContainer = styled('div')({
  width: '100%',
  height: `${SINGLE_TILE_HEIGHT}px`,
  minHeight: `${SINGLE_TILE_HEIGHT}px`,
  maxHeight: `${SINGLE_TILE_HEIGHT}px`,
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  position: 'relative',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  flexShrink: 0,
  flexGrow: 0,
  '& > div[data-controls="true"]': {
    opacity: 0,
    transform: 'translateY(10px)',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
    pointerEvents: 'none',
  },
  '&:hover > div[data-controls="true"]': {
    opacity: 1,
    transform: 'translateY(0)',
    pointerEvents: 'auto',
  },
  '&.speaking': {
    border: '3px solid rgba(0, 255, 0, 0.8)',
    boxShadow: '0 0 20px rgba(0, 255, 0, 0.4)',
    animation: 'pulse-green 1.5s ease-in-out infinite alternate',
  },
  '&.local-speaking': {
    border: '3px solid rgba(0, 255, 0, 1)',
    boxShadow: '0 0 25px rgba(0, 255, 0, 0.6)',
    animation: 'pulse-green-local 1.5s ease-in-out infinite alternate',
  },
  '@keyframes pulse-green': {
    '0%': {
      borderColor: 'rgba(0, 255, 0, 0.8)',
      boxShadow: '0 0 20px rgba(0, 255, 0, 0.4)',
    },
    '100%': {
      borderColor: 'rgba(0, 255, 0, 1)',
      boxShadow: '0 0 30px rgba(0, 255, 0, 0.6)',
    },
  },
  '@keyframes pulse-green-local': {
    '0%': {
      borderColor: 'rgba(0, 255, 0, 1)',
      boxShadow: '0 0 25px rgba(0, 255, 0, 0.6)',
    },
    '100%': {
      borderColor: 'rgba(0, 255, 0, 1)',
      boxShadow: '0 0 35px rgba(0, 255, 0, 0.8)',
    },
  },
})

const CameraControlsContainer = styled('div')({
  position: 'absolute',
  bottom: '8px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '6px',
  padding: '4px',
  borderRadius: '12px',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(4px)',
  zIndex: 10,
})

const ParticipantName = styled('div')({
  position: 'absolute',
  bottom: '8px',
  left: '8px',
  color: 'white',
  fontSize: '12px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  padding: '4px 8px',
  borderRadius: '4px',
  zIndex: 1,
  backdropFilter: 'blur(2px)',
  fontWeight: 500,
})

/* Camera-off placeholder */
const CameraOffPlaceholder = styled('div')({
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(30, 30, 30, 0.95)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
})

const CameraOffLabel = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0.5,
})

/* Conference Mode Grids */
const ConferenceCameraGrid = styled('div')({
  width: '400px',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: `${PANEL_TILE_GAP}px`,
  padding: `${PANEL_TILE_GAP}px`,
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
    width: '0',
    height: '0',
  },
})

const ConferenceCameraRow = styled('div')({
  display: 'flex',
  gap: `${PANEL_TILE_GAP}px`,
  width: '100%',
  minHeight: `${CONF_ROW_HEIGHT}px`,
})

const ConferenceCameraContainer = styled('div')({
  flex: '1 1 0%',
  height: `${CONF_ROW_HEIGHT}px`,
  minHeight: `${CONF_ROW_HEIGHT}px`,
  maxHeight: `${CONF_ROW_HEIGHT}px`,
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  position: 'relative',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  flexShrink: 0,
  '& > div[data-controls="true"]': {
    opacity: 0,
    transform: 'translateY(10px)',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
    pointerEvents: 'none',
  },
  '&:hover > div[data-controls="true"]': {
    opacity: 1,
    transform: 'translateY(0)',
    pointerEvents: 'auto',
  },
  '&.speaking': {
    border: '3px solid rgba(0, 255, 0, 0.8)',
    boxShadow: '0 0 20px rgba(0, 255, 0, 0.4)',
    animation: 'pulse-green 1.5s ease-in-out infinite alternate',
  },
  '&.local-speaking': {
    border: '3px solid rgba(0, 255, 0, 1)',
    boxShadow: '0 0 25px rgba(0, 255, 0, 0.6)',
    animation: 'pulse-green-local 1.5s ease-in-out infinite alternate',
  },
  '@keyframes pulse-green': {
    '0%': {
      borderColor: 'rgba(0, 255, 0, 0.8)',
      boxShadow: '0 0 20px rgba(0, 255, 0, 0.4)',
    },
    '100%': {
      borderColor: 'rgba(0, 255, 0, 1)',
      boxShadow: '0 0 30px rgba(0, 255, 0, 0.6)',
    },
  },
  '@keyframes pulse-green-local': {
    '0%': {
      borderColor: 'rgba(0, 255, 0, 1)',
      boxShadow: '0 0 25px rgba(0, 255, 0, 0.6)',
    },
    '100%': {
      borderColor: 'rgba(0, 255, 0, 1)',
      boxShadow: '0 0 35px rgba(0, 255, 0, 0.8)',
    },
  },
})

const ScrollIndicator = styled('div')<{ show: boolean }>(({ show }) => ({
  position: 'absolute',
  bottom: '0',
  left: '0',
  right: '0',
  height: '20px',
  background: show ? 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))' : 'transparent',
  pointerEvents: 'none',
  transition: 'background 0.3s ease',
  zIndex: 10,
}))

const MuteIndicator = styled('div')({
  position: 'absolute',
  top: '8px',
  right: '8px',
  backgroundColor: 'rgba(244, 67, 54, 0.9)',
  color: 'white',
  borderRadius: '50%',
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  '& svg': {
    width: '14px',
    height: '14px',
  },
})
